import { Autocomplete, Checkbox, FormControl, TextField } from "@mui/material";
import React, { useRef, useState, useEffect, useMemo } from "react";
import CustomArrowSVG from "../../../common/CustomArrowSVG";

const CountryFilter = ({ sortedOptions, handleCountryChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const allOption = useMemo(() => ({ id: "All", label: "All" }), []);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const processedSortedOptions = useMemo(
    () => [allOption, ...sortedOptions],
    [sortedOptions, allOption]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (event, newValue) => {
    const hasAll = newValue.some((opt) => opt.id === "All");
    const prevHasAll = selectedOptions.some((opt) => opt.id === "All");

    let processedValue;

    if (hasAll) {
      if (!prevHasAll) {
        // Selecting All for the first time, replace all selections with All
        processedValue = [allOption];
      } else {
        // Deselecting All by removing it from the selection
        processedValue = newValue.filter((opt) => opt.id !== "All");
      }
    } else {
      processedValue = newValue;
    }

    setSelectedOptions(processedValue);
    handleCountryChange(processedValue);
  };

  return (
    <FormControl
      fullWidth
      variant="outlined"
      sx={{
        boxShadow: "1px 2px 6px #45404060",
        borderRadius: "8px",
        background: "#F5F8FE",
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            border: "0px solid black",
          },
          "&:hover fieldset": {
            borderColor: "#6B9DFE",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#607d8b",
            borderWidth: "2px",
          },
        },
      }}
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={processedSortedOptions}
        getOptionLabel={(option) => option.label}
        onChange={handleChange}
        value={selectedOptions}
        open={open}
        popupIcon={<CustomArrowSVG />}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        openOnFocus
        renderTags={(selectedOptions) =>
          selectedOptions.length === 1 && selectedOptions[0].id === "All"
            ? selectedOptions[0].label
            : `${selectedOptions.length} selected`
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Countries"
            variant="outlined"
            InputLabelProps={{
              sx: {
                color: "#474747",
                fontSize: "16px",
                fontWeight: "500",
                fontFamily: "Instrument_Sans",
                lineHeight: "20px",
                "&.Mui-focused": {
                  color: "black",
                },
                cursor: "pointer",
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            key={option.id}
            style={{
              padding: "5px",
              color: "#333",
              fontSize: "14px",
              borderBottom: "1px solid #9E9E9E",
            }}
          >
            <Checkbox
              checked={selected}
              sx={{
                color: "#007BFF",
                "&.Mui-checked": {
                  color: "#007BFF",
                },
              }}
            />
            {option.label}
          </li>
        )}
        ListboxProps={{
          sx: {
            maxHeight: "240px",
            backgroundColor: "#f5f8fe",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#F5F8FE",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#004571",
              borderRadius: "4px",
            },
          },
        }}
        sx={{
          "& .MuiAutocomplete-popupIndicator": {
            color: "#707275",
            cursor: "pointer",
          },
          "& .MuiAutocomplete-clearIndicator": {
            color: "#707275",
          },
          "& .MuiOutlinedInput-root": {
            padding: "10px 12px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            borderRadius: "8px",
            cursor: "pointer",
          },
          "& .MuiOutlinedInput-input": {
            cursor: "pointer",
          },
        }}
      />
    </FormControl>
  );
};

export default CountryFilter;
